import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Search, Download, Filter, X, ChevronDown, ArrowUpDown, Building2, Upload, CheckCircle, AlertCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import useBranchStore from "@/store/branchStore";
import useAuthStore from "@/store/authStore";
import LogDamageDialog from "@/components/LogDamageDialog";
import { AgingReportPanel } from "@/components/AgingReportPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple category name lookup for display
const RM_CATEGORY_NAMES = {
  "INP": "In-house Plastic",
  "ACC": "Accessories",
  "ELC": "Electric Components",
  "SP": "Spares",
  "BS": "Brand Assets",
  "PM": "Packaging",
  "LB": "Labels",
  "INM": "Input Materials",
  "STK": "Stickers",
  "POLY": "Polymer Grades",
  "MB": "Master Batch",
  "PWD": "Powder Coating",
  "PIPE": "Metal Pipes"
};

const RawMaterials = () => {
  const { selectedBranch } = useBranchStore();
  const { hasRole, isMasterAdmin } = useAuthStore();
  const isAdmin = isMasterAdmin();
  
  const [materials, setMaterials] = useState([]);
  const [branchInventory, setBranchInventory] = useState({});
  const [loading, setLoading] = useState(false);

  // Branch filter - explicit dropdown
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState(selectedBranch || "");
  // Aging report panel reuses the page branch filter as-is (branch name).
  const rmAgingParams = useMemo(() => (branchFilter ? { branch: branchFilter } : {}), [branchFilter]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  // Feb 2026 — Phase 1 migration (defect A on this page). reqId
  // monotonic counter drops any late/out-of-order response, and the
  // dedicated `debouncedSearch` state lets non-search filters take
  // effect immediately while the search string is debounced.
  const fetchReqIdRef = useRef(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const pageSize = 100;

  // Hide RMs with zero stock — defaults ON so the page lands on what's
  // actually in inventory. Only honoured server-side when a branch is
  // selected; falls back to client-side filtering for the "All Branches"
  // view where the backend doesn't compute per-row stock.
  const [hideZeroStock, setHideZeroStock] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    type: "",
    model: "",
    colour: "",
    brand: ""
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    types: [],
    models: [],
    colours: [],
    brands: []
  });
  const [showFilters, setShowFilters] = useState(false);

  // Import dialog state — mirrors FG Stock View (Inventory.js)
  const [importDialog, setImportDialog] = useState(false);
  const [importMode, setImportMode] = useState('add');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Damage logging
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchBranches();
  }, []);

  useEffect(() => {
    // Initialize branch filter from store if available
    if (selectedBranch && !branchFilter) {
      setBranchFilter(selectedBranch);
    }
  }, [selectedBranch]);

  // Debounce the search string — non-search filter changes still fire
  // immediately below (they don't type-race the way free-text does).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 250);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    fetchMaterials();
  }, [currentPage, filters.category, filters.type, filters.model,
      filters.colour, filters.brand, debouncedSearch, branchFilter,
      hideZeroStock]);

  useEffect(() => {
    fetchBranchInventory();
  }, [branchFilter, materials]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`);
      const activeOnly = (response.data || []).filter(b => b.is_active !== false);
      setBranches(activeOnly);
    } catch (error) {
      console.error("Failed to fetch branches");
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/raw-materials/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch filter options");
    }
  };

  const fetchBranchInventory = async () => {
    if (!branchFilter || materials.length === 0) return;
    try {
      const response = await axios.get(`${API}/raw-materials?branch=${encodeURIComponent(branchFilter)}`);
      const inventoryMap = {};
      response.data.forEach(rm => {
        inventoryMap[rm.rm_id] = rm.current_stock || 0;
      });
      setBranchInventory(inventoryMap);
    } catch (error) {
      console.error("Failed to fetch branch inventory");
    }
  };

  const fetchMaterials = async () => {
    const myReqId = ++fetchReqIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (branchFilter) params.append('branch', branchFilter);
      // Send the zero-stock filter only when we have a branch to compare
      // against; without a branch the server has no per-row stock value.
      if (branchFilter && hideZeroStock) params.append('min_stock_only', 'true');
      // Feb 2026 — debounced search string is the fetch input (not the
      // live input, which can be one keystroke ahead of the last fired
      // request).
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (filters.category) params.append('category', filters.category);
      if (filters.type) params.append('type_filter', filters.type);
      if (filters.model) params.append('model_filter', filters.model);
      if (filters.colour) params.append('colour_filter', filters.colour);
      if (filters.brand) params.append('brand_filter', filters.brand);

      const response = await axios.get(`${API}/raw-materials/filtered?${params.toString()}`);
      // Stale-response guard — kills the wrong-results-flash bug when
      // an older response lands after a newer one under throttled net.
      if (myReqId !== fetchReqIdRef.current) return;
      setMaterials(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      if (myReqId !== fetchReqIdRef.current) return;
      toast.error("Failed to fetch raw materials");
    } finally {
      if (myReqId === fetchReqIdRef.current) setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: "", category: "", type: "", model: "", colour: "", brand: "" });
    setCurrentPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      const params = new URLSearchParams();
      // Pass branch filter - if empty, exports all branches with Branch ID column
      if (branchFilter) params.append('branch', branchFilter);
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.type) params.append('type_filter', filters.type);
      if (filters.model) params.append('model_filter', filters.model);
      if (filters.colour) params.append('colour_filter', filters.colour);
      if (filters.brand) params.append('brand_filter', filters.brand);

      const response = await axios.get(`${API}/raw-materials/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const filename = response.headers['content-disposition']?.split('filename=')[1] || 'raw_materials_export.xlsx';
      saveAs(blob, filename);
      
      if (branchFilter) {
        toast.success(`Exported ${totalItems} RMs for ${branchFilter}`);
      } else {
        toast.success(`Exported RMs across all branches`);
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export raw materials");
    }
  };



  // ----- Template download + Import (mirrors FG Stock View) -----
  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory/rm/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'rm_inventory_template.xlsx');
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Template download failed:', error);
      toast.error('Failed to download template');
    }
  };

  const openImportDialog = () => {
    setImportMode('add');
    setImportFile(null);
    setImportResult(null);
    setImportDialog(true);
  };

  const downloadRmSnapshot = async (label) => {
    // Pull the *currently filtered* RM inventory (branch-scoped) into an Excel
    // and force-download it. Mirrors the FG-side safety net so destructive
    // imports (Replace mode) always have a rollback baseline. The file shape
    // matches the bulk-import template, so it can be re-uploaded with
    // mode=replace to undo an unwanted import.
    try {
      const params = new URLSearchParams();
      if (branchFilter) params.append('branch', branchFilter);
      const response = await axios.get(`${API}/inventory/rm/export?${params}`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const branch = (branchFilter || 'all').replace(/\s+/g, '_');
      saveAs(new Blob([response.data]), `rm_inventory_PRE_${label}_${branch}_${ts}.xlsx`);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImporting(true);
    setImportResult(null);

    // SAFETY NET: when the user picks "Replace Stock" (destructive), grab a
    // snapshot of the current branch RM inventory first. The snapshot file is
    // downloaded *before* the import is fired — if anything goes wrong, the
    // user has a complete pre-import baseline to re-upload via Replace mode.
    if (importMode === 'replace') {
      const snapshotOk = await downloadRmSnapshot('REPLACE');
      if (!snapshotOk) {
        toast.error(
          "Couldn't download the pre-import snapshot. Aborting Replace to protect your data — please retry or use 'Add to Existing' mode.",
          { duration: 8000 }
        );
        setImporting(false);
        return;
      }
      toast.info(
        "Pre-import snapshot downloaded. Keep that file safe — it's your rollback baseline.",
        { duration: 6000 }
      );
    }

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { uploadWithReceipt } = await import('../lib/bulkUploadReceipt');
      const data = await uploadWithReceipt(
        `${API}/inventory/rm/bulk-import?mode=${importMode}`,
        formData,
        {
          axiosConfig: {
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          },
          showToast: false, // we keep our own legacy toast below
        }
      );
      setImportResult(data);
      if (data.success) {
        toast.success(`Import complete: ${data.processed} records processed — Receipt downloaded.`);
        // Refresh the list and the per-branch inventory map
        fetchMaterials();
        fetchBranchInventory();
      } else {
        toast.error('Import completed with errors — see downloaded receipt for details.');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Import failed';
      toast.error(msg);
      setImportResult({ errors: [msg], success: false });
    }
    setImporting(false);
  };

  // Filter dropdown component
  const FilterDropdown = ({ label, value, options, onChange, placeholder }) => {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    
    const filteredOptions = options.filter(opt => 
      opt.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <div className="relative">
        <Label className="text-xs uppercase tracking-wider font-bold mb-1 block">{label}</Label>
        <div 
          className="flex h-9 w-full rounded-sm border border-input bg-white px-3 py-2 text-sm cursor-pointer items-center justify-between"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={value ? "text-zinc-900" : "text-zinc-400"}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        </div>
        
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b">
              <Input
                placeholder="Type to search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-40 overflow-y-auto">
              <div 
                className="px-3 py-2 hover:bg-zinc-50 cursor-pointer text-sm text-zinc-500"
                onClick={() => { onChange(""); setIsOpen(false); setSearch(""); }}
              >
                Clear filter
              </div>
              {filteredOptions.map((opt, idx) => (
                <div 
                  key={idx}
                  className={`px-3 py-2 hover:bg-zinc-50 cursor-pointer text-sm ${value === opt ? 'bg-zinc-100 font-medium' : ''}`}
                  onClick={() => { onChange(opt); setIsOpen(false); setSearch(""); }}
                >
                  {opt}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-zinc-400">No matches</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8" data-testid="raw-materials-page">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">RM Stock View</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Branch inventory levels • {branchFilter || "All Branches"} • {totalItems} items
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Branch Filter Dropdown */}
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <Select 
              value={branchFilter || "_all"} 
              onValueChange={(v) => {
                setBranchFilter(v === "_all" ? "" : v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]" data-testid="branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All Branches</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" onClick={handleExport} className="uppercase text-xs tracking-wide" data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Export {branchFilter ? branchFilter : "All"}
          </Button>
          <AgingReportPanel kind="rm" params={rmAgingParams} branchLabel={branchFilter} />
          {/* Hide-zero-stock toggle — pill switch matching the existing button look. */}
          <Button
            variant={hideZeroStock ? "default" : "outline"}
            size="sm"
            onClick={() => { setHideZeroStock(v => !v); setCurrentPage(1); }}
            className="uppercase text-xs tracking-wide"
            data-testid="hide-zero-stock-toggle"
            title={hideZeroStock ? "Showing only RMs with stock > 0" : "Showing all RMs (incl. zero stock)"}
          >
            {hideZeroStock ? "Stock > 0" : "All RMs"}
          </Button>
          {/* Bulk Template / Import moved to /inventory-imports (admin-only, May 2026) */}
          <PermButton
            entity="DamageLog"
            action="CREATE"
            variant="destructive"
            onClick={() => setDamageDialogOpen(true)}
            className="uppercase text-xs tracking-wide"
            data-testid="rm-log-damage-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Log Damage
          </PermButton>

        </div>
      </div>

      {/* Damage logging dialog */}
      <LogDamageDialog
        open={damageDialogOpen}
        onOpenChange={setDamageDialogOpen}
        itemType="RM"
        branches={branches}
        defaultBranch={branchFilter || selectedBranch || ''}
        onLogged={() => fetchMaterials()}
      />

      {/* Search and Filter Bar */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input 
              placeholder="Search by RM ID, description, or category…" 
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 font-mono"
              data-testid="rm-inventory-search-input"
            />
          </div>
          <Button 
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="uppercase text-xs"
          >
            <Filter className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" onClick={clearFilters} className="text-xs">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
            <FilterDropdown
              label="Category"
              value={filters.category}
              options={filterOptions.categories}
              onChange={(v) => handleFilterChange('category', v)}
              placeholder="All categories"
            />
            <FilterDropdown
              label="Type"
              value={filters.type}
              options={filterOptions.types}
              onChange={(v) => handleFilterChange('type', v)}
              placeholder="All types"
            />
            <FilterDropdown
              label="Model"
              value={filters.model}
              options={filterOptions.models}
              onChange={(v) => handleFilterChange('model', v)}
              placeholder="All models"
            />
            <FilterDropdown
              label="Colour"
              value={filters.colour}
              options={filterOptions.colours}
              onChange={(v) => handleFilterChange('colour', v)}
              placeholder="All colours"
            />
            <FilterDropdown
              label="Brand"
              value={filters.brand}
              options={filterOptions.brands}
              onChange={(v) => handleFilterChange('brand', v)}
              placeholder="All brands"
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-border bg-white rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="rm-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">RM ID</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Category</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Description</th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider" title={branchFilter ? "Stock at the selected branch" : "Sum of stock across all branches"}>
                  {branchFilter ? "Branch Inventory" : "Total Stock (All Branches)"}
                </th>
                <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider" title={branchFilter ? "Moving Weighted Average Cost (MWAC) for this branch — updates atomically on every inward" : "Stock-weighted blended MWAC across all branches"}>
                  Unit Cost (MWAC)
                </th>
                <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider" title={branchFilter ? "Branch stock × Unit Cost (MWAC)" : "Sum across all branches of (qty × that branch's MWAC)"}>
                  Inventory Value
                </th>
                <th
                  className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider"
                  title="Bidso-supplied stock currently held at job-worker vendors (all vendors summed). Detail: Job Work Balances page."
                >
                  At Job Worker
                </th>
                <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Safety Stock</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Server already drops zero-stock rows when both
                // `branch` and `hideZeroStock` are set. For the
                // "All Branches" view (or as a defensive second pass
                // before redeploy on prod), filter again here using
                // whichever stock signal each row carries.
                const visible = hideZeroStock
                  ? materials.filter((m) => {
                      const s = m.branch_stock ?? branchInventory[m.rm_id] ?? 0;
                      return Number(s) > 0;
                    })
                  : materials;
                if (!loading && visible.length === 0 && materials.length > 0) {
                  return (
                    <tr>
                      <td colSpan={8} className="p-12 text-center text-muted-foreground font-mono text-sm">
                        Every RM on this page has zero stock. Toggle "All RMs" to see them.
                      </td>
                    </tr>
                  );
                }
                return visible.map((material) => {
                const currentStock = material.branch_stock ?? branchInventory[material.rm_id] ?? 0;
                const isBelowSafety = currentStock < material.low_stock_threshold;
                return (
                  <tr key={material.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                    <td className={`p-4 align-middle font-mono text-sm font-bold ${isBelowSafety ? 'text-red-600' : 'text-zinc-700'}`}>
                      {material.rm_id}
                    </td>
                    <td className="p-4 align-middle">
                      <div className="text-xs font-mono text-primary font-bold">{material.category}</div>
                      <div className="text-xs text-muted-foreground">{RM_CATEGORY_NAMES[material.category] || material.category}</div>
                    </td>
                    <td className="p-4 align-middle text-sm text-zinc-700 max-w-[300px]">
                      <span className="truncate block" title={material.description || material.category_data?.name || '-'}>
                        {material.description || material.category_data?.name || '-'}
                      </span>
                    </td>
                    <td className={`p-4 align-middle font-mono ${isBelowSafety ? 'text-red-600 font-bold' : 'text-zinc-700'}`}>
                      {Number.isFinite(Number(currentStock))
                        ? Number(currentStock).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : currentStock}
                    </td>
                    <td
                      className="p-4 align-middle font-mono text-right text-zinc-700"
                      data-testid={`rm-wac-${material.rm_id}`}
                      title={material.wac_last_updated ? `Updated ${new Date(material.wac_last_updated).toLocaleString()}` : (material.is_aggregate ? "Stock-weighted MWAC across all branches" : "No MWAC yet — run the backfill or wait for the next inward")}
                    >
                      {Number(material.branch_wac || 0) > 0 ? (
                        <span>Rs {Number(material.branch_wac).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td
                      className="p-4 align-middle font-mono text-right text-zinc-900 font-semibold"
                      data-testid={`rm-inv-value-${material.rm_id}`}
                      title={
                        branchFilter
                          ? `Branch stock × Unit Cost (source: ${material.cost_source || 'mwac'})`
                          : `Sum across all branches of (qty × cost). Cost source: ${material.cost_source || 'mwac'}`
                      }
                    >
                      {Number(material.inventory_value || 0) > 0 ? (
                        <span>Rs {Math.round(Number(material.inventory_value)).toLocaleString()}</span>
                      ) : (
                        <span className="text-zinc-400 font-normal">—</span>
                      )}
                    </td>
                    <td className="p-4 align-middle font-mono text-zinc-700">{material.low_stock_threshold}</td>
                  </tr>
                );
              });
              })()}
            </tbody>
          </table>
          {loading && (
            <div className="p-12 text-center text-muted-foreground font-mono text-sm">Loading...</div>
          )}
          {!loading && materials.length === 0 && (
            <div className="p-12 text-center text-muted-foreground font-mono text-sm">
              No raw materials found. Try adjusting filters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-zinc-200 flex items-center justify-between">
            <p className="text-sm text-zinc-500 font-mono">
              Page {currentPage} of {totalPages} • {totalItems} items
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Import RM Inventory Dialog — mirrors FG Stock View */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="rm-import-dialog">
          <DialogHeader>
            <DialogTitle>Import RM Inventory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Import Mode</label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="rm-import-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex flex-col">
                      <span className="font-medium">Add to Existing</span>
                      <span className="text-xs text-gray-500">Quantities will be added to current stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex flex-col">
                      <span className="font-medium">Replace Stock</span>
                      <span className="text-xs text-gray-500">Uploaded quantities will replace current stock</span>
                      <span className="text-[10px] text-amber-700 font-medium">⚠️ Pre-import snapshot auto-downloaded</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Select Excel File</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="hidden"
                  id="rm-import-file"
                  data-testid="rm-import-file-input"
                />
                <label htmlFor="rm-import-file" className="cursor-pointer">
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span>{importFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2" />
                      <p>Click to select file</p>
                      <p className="text-xs">.xlsx or .xls</p>
                    </div>
                  )}
                </label>
              </div>
              <Button variant="link" size="sm" onClick={downloadTemplate} className="text-xs" data-testid="rm-import-dialog-template-link">
                Download template with reference data
              </Button>
            </div>

            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-start gap-2">
                  {importResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {importResult.success ? 'Import Successful' : 'Import Failed'}
                    </p>
                    {importResult.processed > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        Processed: {importResult.processed} | Added: {importResult.added ?? 0} | Updated: {importResult.updated ?? 0}
                      </p>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto">
                        <p className="text-sm font-medium text-red-700">Errors:</p>
                        {importResult.errors.slice(0, 10).map((err, idx) => (
                          <p key={idx} className="text-xs text-red-600">{err}</p>
                        ))}
                        {importResult.errors.length > 10 && (
                          <p className="text-xs text-red-600">...and {importResult.errors.length - 10} more</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!importFile || importing} data-testid="rm-confirm-import-btn">
              {importing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>) : (<><Upload className="h-4 w-4 mr-2" /> Import</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RawMaterials;
